import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Instagram, Linkedin, Mail, ExternalLink, Play, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import '../styles/Home.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [email, setEmail] = useState('');
  const [activeSection, setActiveSection] = useState('home');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch content from database
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API}/content`);
        const data = await response.json();
        
        // Organize content by section and key
        const contentMap = {};
        data.forEach(item => {
          if (!contentMap[item.section]) {
            contentMap[item.section] = {};
          }
          contentMap[item.section][item.key] = item.value;
        });
        
        setContent(contentMap);
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
  }, []);

  // Helper function to get content
  const getContent = (section, key, fallback = '') => {
    return content[section]?.[key] || fallback;
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle scrollspy for active nav
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'tarot', 'about', 'press', 'projects'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will connect to backend later
    setTimeout(() => {
      setSubmitMessage('Thank you! You\'re on the list.');
      setEmail('');
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 3000);
    }, 1000);
  };

  return (
    <>
      <SEO />
      {loading ? (
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#EAE5DE'
        }}>
          <div style={{ textAlign: 'center', color: '#6B3F3F' }}>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '24px' }}>Loading...</h2>
          </div>
        </div>
      ) : (
      <div className="highest-good-ventures">
      {/* Sticky Navigation */}
      <nav className="sticky-nav">
        <div className="nav-container">
          <div className="nav-logo">HIGHEST GOOD VENTURES</div>
          
          <div className="nav-menu">
            <a
              href="#about"
              onClick={(e) => {e.preventDefault();scrollToSection('about');}}
              className={activeSection === 'about' ? 'active' : ''}>

              About
            </a>
            <a
              href="#tarot"
              onClick={(e) => {e.preventDefault();scrollToSection('tarot');}}
              className={activeSection === 'tarot' ? 'active' : ''}>

              Tarot for Bros™
            </a>
            <a
              href="#press"
              onClick={(e) => {e.preventDefault();scrollToSection('press');}}
              className={activeSection === 'press' ? 'active' : ''}>

              Press
            </a>
          </div>

          <Button
            className="nav-cta"
            onClick={() => window.open('https://www.tarotforbros.com/', '_blank')}>

            Join Waitlist →
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-container-two-col">
          <div className="hero-content">
            <h1 className="hero-headline">
              Creating products, brands & experiences that return people to themselves.
            </h1>
            <p className="hero-intro-text">
              Highest Good Ventures is founded by Kristen Barnett, a serial entrepreneur and former management consultant based in Mill Valley, CA. Kristen Barnett's flagship venture is Tarot for Bros™, a tarot card company with the mission to make self-reflection approachable and culturally relevant for men.
            </p>
            <div className="hero-ctas">
              <Button
                className="cta-primary"
                onClick={() => window.open('https://www.tarotforbros.com/', '_blank')}>

                Join the Tarot for Bros™ Waitlist
              </Button>
              <Button
                variant="outline"
                className="cta-secondary"
                onClick={() => window.location.href = '/about-kristen-barnett'}>

                About Kristen Barnett
              </Button>
            </div>
          </div>
          
          <div className="hero-image">
            <img
              src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/c627rddg_Headshot_Updated.jpeg"
              alt="Kristen Barnett, founder of Highest Good Ventures"
              className="portrait-image" />

          </div>
        </div>
      </section>

      {/* Philosophy Block */}
      <div className="philosophy-block">
        <div className="section-container">
          <h2 className="section-heading-center">{getContent('philosophy', 'heading', 'ABOUT HIGHEST GOOD VENTURES')}</h2>
          <div className="philosophy-content">
            <p className="philosophy-text">
              {getContent('philosophy', 'paragraph_1', 'Highest Good Ventures is built on the idea that the purpose of life — and of work — is to strive toward the highest good. In the tradition of Greek philosophy, the highest good is not wealth, fame, or even success by conventional measures. It is eudaimonia — a flourishing life — achieved through virtuous activity in alignment with reason, sustained over time.')}
            </p>
            <p className="philosophy-text">
              {getContent('philosophy', 'paragraph_2', 'We build products, services, and experiences that bring people back to themselves — back to nature, back to community, back to a sense of purpose. At a moment when people are drifting from each other and from meaning, we think that\'s the most important work there is.')}
            </p>
            <p className="philosophy-text">
              {getContent('philosophy', 'paragraph_3', 'Our current flagship venture is Tarot for Bros™.')}
            </p>
          </div>
        </div>
      </div>

      {/* Thin Divider */}
      <div className="thin-divider"></div>

      {/* About Kristen Section */}
      <section id="about" className="about-section-tight">
        <div className="section-container">
          <h2 className="section-heading-center !text-lg">{getContent('about_kristen', 'heading', 'ABOUT KRISTEN BARNETT')}</h2>
          
          <div className="about-grid-single">
            <div className="about-image-wide">
              <img
                src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/xkmg5bbt_Green%20Shirt%20Headshot%20Cropped.jpeg"
                alt="Kristen Barnett, founder of Highest Good Ventures"
                className="rounded-image" />

            </div>
            
            <div className="about-content-full">
              <p className="about-bio">
                {getContent('about_kristen', 'bio_paragraph_1', 'Kristen Barnett is a serial entrepreneur, tarot card reader and founder of Highest Good Ventures — an incubator at the intersection of the spiritual economy and regenerative capitalism. Her work has been featured in The New Yorker, Business Insider, and EATER.')}
              </p>
              <p className="about-bio">
                {getContent('about_kristen', 'bio_paragraph_2', 'After studying business at Cornell, Kristen began her career at The Boston Consulting Group. While there, a prolonged battle with chronic Lyme Disease — and the profound healing she ultimately found through alternative medicine — set her on a different path entirely. She left consulting with a sense of higher calling and spent the next decade building in the food industry, founding the acclaimed "anti-ghost-kitchen" Hungry House and co-founding Culinary Creators Worldwide, an experiential marketing agency.')}
              </p>
              <p className="about-bio">
                {getContent('about_kristen', 'bio_paragraph_3', 'A subsequent burnout and spiritual reckoning reshaped everything she thought she knew. Today, Kristen channels her strategic creativity toward ventures that align economic potential, integrity, and long-term impact — building experiences and products that reconnect people with ancient human truths. Highest Good Ventures is her incubator for exactly this kind of work — ventures built at the intersection of ancient wisdom and modern business. One of these is Tarot for Bros™, a tarot card company creating decks for men bringing mythic self-inquiry to modern men. Kristen writes a weekly Substack called Diaries of a Woo Woo Professional and lives in Mill Valley, California.')}
              </p>
              
              <div className="about-buttons">
                <Button
                  className="cta-primary"
                  onClick={() => window.location.href = '/about-kristen-barnett'}>

                  Get Full Bio
                </Button>
                <Button
                  variant="outline"
                  className="cta-secondary"
                  onClick={() => window.open('https://www.instagram.com/itskristenbarnett/', '_blank')}>

                  <Instagram size={18} />
                  Follow on Instagram
                </Button>
                <Button
                  variant="outline"
                  className="cta-secondary"
                  onClick={() => window.open('https://itskristen.substack.com/?r=6db7y9&utm_campaign=pub-share-checklist', '_blank')}>

                  <Mail size={18} />
                  Subscribe on Substack
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Press Features Section - Moved closer */}
        <div id="press" className="press-section-inline">
          <div className="section-container">
            <h2 className="section-heading-center">{getContent('about_kristen', 'press_heading', 'AS FEATURED IN')}</h2>
            
            <div className="press-logos-horizontal">
              <a
                href="https://www.newyorker.com/magazine/2022/05/23/hungry-house-an-improvement-on-the-ghost-kitchen"
                target="_blank"
                rel="noopener noreferrer"
                className="press-logo-link">

                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/35xo6w42_The_New_Yorker_logo.png"
                  alt="The New Yorker"
                  className="press-logo-img" />

              </a>
              
              <a
                href="https://www.businessinsider.com/foodtech-startups-delivery-restaurants-industry-recession-2022-11"
                target="_blank"
                rel="noopener noreferrer"
                className="press-logo-link">

                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/xq5s5yv0_Business_Insider_Logo.png"
                  alt="Business Insider"
                  className="press-logo-img" />

              </a>
              
              <a
                href="https://youtu.be/qY114LRLzx0?si=SM_E-sYnCC90WEJr&t=163"
                target="_blank"
                rel="noopener noreferrer"
                className="press-logo-link">

                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/ia1kc6rg_Inside_Edition_logo.svg"
                  alt="Inside Edition"
                  className="press-logo-img" />

              </a>
              
              <a
                href="https://cherrybombe.com/blogs/the-future-of-food-is-you/hungry-house-founder-ceo-kristen-barnett-the-future-is-ghost-kitchens"
                target="_blank"
                rel="noopener noreferrer"
                className="press-logo-link">

                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/14x5nc5d_Cherry%20bombe%20logo.webp"
                  alt="Cherry Bombe"
                  className="press-logo-img" />

              </a>
            </div>
          </div>
        </div>

        {/* Speaking Gallery Section */}
        <div className="speaking-gallery-section">
          <div className="section-container">
            <div className="speaking-gallery-grid">
              <div className="speaking-gallery-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/md54aku6_Speaking%201.jpg" 
                  alt="Kristen Barnett speaking at conference"
                  className="speaking-gallery-image"
                />
              </div>
              <div className="speaking-gallery-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/1rmaxesg_Speaking%202.jpeg" 
                  alt="Kristen Barnett at speaking event"
                  className="speaking-gallery-image"
                />
              </div>
              <div className="speaking-gallery-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/7pn1aqoc_Speaking%203.jpeg" 
                  alt="Kristen Barnett on panel discussion"
                  className="speaking-gallery-image"
                />
              </div>
              <div className="speaking-gallery-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/0dvuyyid_Speaking%204.jpeg" 
                  alt="Kristen Barnett at CES panel"
                  className="speaking-gallery-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tarot for Bros™ Section */}
      <section id="tarot" className="tarot-section">
        <div className="section-container">
          <div className="tarot-header">
            <p className="tarot-live-badge">{getContent('tarot', 'badge', 'WAITLIST OPEN')}</p>
            <h2 className="section-heading-large">{getContent('tarot', 'heading', 'TAROT FOR BROS™')}</h2>
            <p className="tarot-tagline">{getContent('tarot', 'tagline', 'Ancient wisdom for modern men')}</p>
            <p className="tarot-waitlist-text">{getContent('tarot', 'waitlist_text', 'Join the waitlist to gain access to the first drop.')}</p>
          </div>

          <div className="tarot-ctas-top">
            <Button
              className="cta-primary"
              onClick={() => window.open('https://www.tarotforbros.com/', '_blank')}>

              Join the Waitlist
            </Button>
            <Button
              variant="outline"
              className="cta-secondary"
              onClick={() => window.open('https://www.instagram.com/tarotforbros', '_blank')}>

              <Instagram size={18} />
              Follow on Instagram
            </Button>
            <Button
              variant="outline"
              className="cta-secondary"
              onClick={() => window.open('https://www.tiktok.com/@tarot.for.bros', '_blank')}>

              Follow on TikTok
            </Button>
          </div>
          
          <div className="tarot-description">
            <p>
              {getContent('tarot', 'description_paragraph_1', 'Tarot for Bros™ is a tarot card company that translates the mystical into a language aligned with bro-code—helping men tap into something greater without losing their edge. We create decks and tools for sharpening your inner compass, so you can move through life with more clarity, purpose, and self-trust.')}
            </p>
            <p>
              {getContent('tarot', 'description_paragraph_2', 'For too long, the art of pulling cards has lived in velvet-draped corners, seen as something soft or feminine. Tarot for Bros flips that script—finally creating tarot products for men to access the insight, guidance, and grounded self-reflection they deserve.')}
            </p>
          </div>

          <div className="video-section">
            <Button
              className="video-toggle-btn"
              onClick={() => setShowVideo(!showVideo)}>

              <Play size={18} />
              Watch the Announcement Video
              <ChevronDown size={18} className={showVideo ? 'rotated' : ''} />
            </Button>
            
            {showVideo &&
            <div className="video-container">
                <video
                controls
                className="announcement-video"
                src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/yruujuo8_The%20Launch%20of%20Tarrot%20for%20bros.mov">

                  Your browser does not support the video tag.
                </video>
              </div>
            }
          </div>

          <div className="tarot-hero-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/qk1cd1am_The%20Emperor%20Card.jpeg" 
              alt="Tarot for Bros™ - Tarot Card Company Creating Decks for Men by Kristen Barnett"
              className="hero-image-full"
            />
          </div>
        </div>
      </section>

      {/* Past Projects Section */}
      <section id="work" className="projects-section-light">
        <div className="section-container">
          <div className="projects-header">
            <h2 className="section-heading-large">{getContent('past_projects', 'heading', 'PAST PROJECTS')}</h2>
          </div>

          <div className="projects-grid-two">
            <div className="project-card">
              <div className="project-image">
                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/fk3ou7uh_DSC01325.jpg"
                  alt="Eden Forest Collective"
                  className="card-image" />

              </div>
              <div className="project-content">
                <h3 className="project-title">{getContent('past_projects', 'eden_forest_title', 'Eden Forest Collective')}</h3>
                <p className="project-description">
                  {getContent('past_projects', 'eden_forest_description', 'Eden Forest Collective, founded by Sam Langer and Ruya Günergin, is a regenerative farm and cultural nonprofit in Fillmore, California that brings together ecology, education, and community to explore new models of living in relationship with the Earth. In her work with Eden, Kristen supported the shaping of its strategic vision, programming, and financial model—helping translate its philosophy into a scalable, impact-driven organization.')}
                </p>
                <a
                  href="https://www.edenforestcollective.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link">

                  Learn More <ExternalLink size={14} />
                </a>
              </div>
            </div>

            <div className="project-card">
              <div className="project-image">
                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/jskeycnf_DSC03528.jpg"
                  alt="Farm School Regenerative Retreat"
                  className="card-image" />

              </div>
              <div className="project-content">
                <h3 className="project-title">{getContent('past_projects', 'farm_school_title', 'Farm School Regenerative Retreat')}</h3>
                <p className="project-description">
                  {getContent('past_projects', 'farm_school_description', 'In partnership with Scott Landers of the Landers Family Farm in Cooper, TX, she developed Farm School as a retreat-based concept rooted in regenerative agriculture, designed to explore new models of learning, community, and land stewardship. Her work focused on shaping the vision, business model, and program design.')}
                </p>
                <a
                  href="https://www.farmschoolexperience.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link">

                  Learn More <ExternalLink size={14} />
                </a>
              </div>
            </div>

            <div className="project-card">
              <div className="project-image">
                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/gcflxvzb_1P0A0287.jpg"
                  alt="Culinary Creators Worldwide"
                  className="card-image" />

              </div>
              <div className="project-content">
                <h3 className="project-title">{getContent('past_projects', 'culinary_creators_title', 'Culinary Creators Worldwide')}</h3>
                <p className="project-description">
                  {getContent('past_projects', 'culinary_creators_description', 'Culinary Creators Worldwide blended top culinary talent, food brands, and media partners to create high-impact experiential campaigns and collaborations. Kristen recognized a growing demand for infrastructure that helps digitally native chefs and creators scale their influence and partner with brands in meaningful ways. The company sat at the intersection of food, media, and technology—leveraging events, content, and partnerships to turn culinary creativity into cultural and commercial impact.')}
                </p>
                <a
                  href="https://foodondemand.com/07182023/kristen-barnett-blends-culinary-talent-with-experiential-marketing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link">

                  Learn More <ExternalLink size={14} />
                </a>
              </div>
            </div>

            <div className="project-card">
              <div className="project-image">
                <img
                  src="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/6cq8dgdl_Hungry%20House%20New%20Yorker.webp"
                  alt="Hungry House"
                  className="card-image" />

              </div>
              <div className="project-content">
                <h3 className="project-title">{getContent('past_projects', 'hungry_house_title', 'Hungry House')}</h3>
                <p className="project-description">
                  {getContent('past_projects', 'hungry_house_description', 'Hungry House, the "anti-ghost kitchen" food delivery concept, was founded by Kristen Barnett to bring more quality, creativity, and chef-driven storytelling to the food tech industry. Operating kitchens across New York City, it partnered with standout chefs to scale their concepts through a their integrated kitchen model—reaching new audiences without the overhead of traditional brick-and-mortar. The company was acquired in January 2025.')}
                </p>
                <a
                  href="https://www.newyorker.com/magazine/2022/05/23/hungry-house-an-improvement-on-the-ghost-kitchen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link">

                  Learn More <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          {/* Connect Section - Centered */}
          <div className="footer-connect-centered">
            <h3 className="footer-heading-centered">Connect</h3>
            <div className="social-links-horizontal">
              <a href="https://www.instagram.com/tarotforbros" target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={20} />
                <span>@tarotforbros</span>
              </a>
              <a href="https://www.instagram.com/itskristenbarnett/" target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={20} />
                <span>@itskristenbarnett</span>
              </a>
              <a href="https://www.linkedin.com/in/kcbarnett/" target="_blank" rel="noopener noreferrer" className="social-link">
                <Linkedin size={20} />
                <span>LinkedIn</span>
              </a>
              <a href="https://itskristen.substack.com/?r=6db7y9&utm_campaign=pub-share-checklist" target="_blank" rel="noopener noreferrer" className="social-link">
                <Mail size={20} />
                <span>Substack</span>
              </a>
            </div>
          </div>

          {/* Join the Journey Section - Centered */}
          <div className="footer-newsletter-centered">
            <h3 className="footer-heading-centered">{getContent('footer', 'heading', 'Join the Journey')}</h3>
            <p className="footer-description-centered">
              {getContent('footer', 'description', 'Get updates on Highest Good Ventures, our upcoming drops, and the Tarot for Bros™ presale by subscribing to Kristen\'s substack.')}
            </p>
            
            {/* Substack Embed */}
            <div className="substack-embed-fullwidth">
              <iframe 
                src="https://itskristen.substack.com/embed" 
                width="100%" 
                height="150" 
                style={{ border: 'none', background: 'transparent' }} 
                frameBorder="0" 
                scrolling="no"
                title="Subscribe to Kristen's Substack"
              />
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              {getContent('footer', 'copyright', '© 2026 Highest Good Ventures LLC. Tarot for Bros™ is a subsidiary owned by Tarot for Bros LLC.')}
            </p>
          </div>
        </div>
      </footer>
    </div>
    )}
    </>
  );
};

export default Home;